import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Building2, Clock, Loader2, TrendingDown } from "lucide-react";
import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { PlanChangePreview } from "@/contexts/membership/membership-types";
import { useMembership } from "@/contexts/membership-context";
import { env } from "@/env";
import { usePricingTiers } from "@/hooks/use-pricing-tiers";
import { estimateProration } from "@/lib/proration";
import { cn } from "@/lib/utils";
import type { MembershipTier } from "@/types/membership";
import type { PricingTier } from "@/types/pricing";
import { MembershipBadge } from "./membership-badge";
import { formatCurrency, PricingCard } from "./pricing-card";
import { queryKeys } from "@/lib/query-keys";

// Constants
const MIN_TIERS_FOR_THREE_COLUMN = 3;

type PlanChangeDisplay =
  | {
      direction: "upgrade";
      unusedCredit: number;
      proratedAmount: number;
      fullAmount: number;
      daysRemaining: number;
    }
  | {
      direction: "downgrade";
      trialDays: number;
      savings: number;
      newAmount: number;
    }
  | {
      direction: "cadence_change";
      trialDays: number;
      newAmount: number;
      targetBillingCadence: "monthly" | "yearly";
    };

export type UpgradeDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentTier: MembershipTier;
  onUpgrade: (tierId: string, billingPeriod: "monthly" | "yearly") => Promise<void> | void;
  isLoading?: boolean;
};

export function UpgradeDialog({
  open,
  onOpenChange,
  currentTier,
  onUpgrade,
  isLoading = false,
}: UpgradeDialogProps) {
  const { getPlanChangeType, activeSubscription, billingCadence } = useMembership();
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "yearly">(
    billingCadence ?? "monthly",
  );
  const [selectedTier, setSelectedTier] = useState<string>();
  const [hoveredTierId, setHoveredTierId] = useState<string>();

  const { data: allTiersRaw = [] } = useQuery<PricingTier[]>({
    queryKey: queryKeys.subscriptions.productTiers(),
    queryFn: usePricingTiers,
  });

  // Fetch server-side proration preview when a tier is hovered/selected
  const previewTargetId = selectedTier ?? hoveredTierId;
  const previewChangeType = previewTargetId
    ? getPlanChangeType(previewTargetId, billingPeriod)
    : "same";

  const {
    data: serverPreview,
    error: previewError,
    isLoading: isPreviewLoading,
  } = useQuery<PlanChangePreview>({
    queryKey: queryKeys.subscriptions.planChangePreview(
      activeSubscription?.id,
      previewTargetId,
      billingPeriod,
    ),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (previewTargetId) {
        params.set("targetTierId", previewTargetId);
      }
      params.set("billingPeriod", billingPeriod);
      const response = await fetch(
        `${env.VITE_API_URL}/api/subscriptions/preview-upgrade?${params.toString()}`,
        { credentials: "include" },
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.statusMessage || errorData.message || "Failed to fetch Plan change preview",
        );
      }
      return response.json() as Promise<PlanChangePreview>;
    },
    enabled: !!activeSubscription && !!previewTargetId,
    staleTime: 30_000,
  });

  // Client-side estimate as instant placeholder
  const clientEstimate = useMemo(() => {
    if (!(activeSubscription && previewTargetId)) {
      return null;
    }

    const targetTier = allTiersRaw.find((t) => t.id === previewTargetId);
    if (!targetTier) {
      return null;
    }

    const currentAmount =
      typeof activeSubscription.metadata?.billingAmount === "number"
        ? activeSubscription.metadata.billingAmount
        : 0;
    const newAmount = targetTier.price[billingPeriod];
    const periodStart = activeSubscription.currentPeriodStart ?? new Date();
    const periodEnd = activeSubscription.currentPeriodEnd ?? new Date();

    return estimateProration({
      currentAmount,
      newAmount,
      currentPeriodStart: periodStart instanceof Date ? periodStart : new Date(periodStart),
      currentPeriodEnd: periodEnd instanceof Date ? periodEnd : new Date(periodEnd),
    });
  }, [activeSubscription, previewTargetId, allTiersRaw, billingPeriod]);

  // Normalize server and client shapes into a common display type
  const planChangeDisplay: PlanChangeDisplay | null = useMemo(() => {
    if (serverPreview) {
      if (serverPreview.direction === "downgrade") {
        return {
          direction: "downgrade",
          trialDays: serverPreview.trialDays ?? 0,
          savings: serverPreview.savings ?? 0,
          newAmount: serverPreview.firstPayment.amount,
        };
      }
      if (serverPreview.direction === "cadence_change") {
        return {
          direction: "cadence_change",
          trialDays: serverPreview.trialDays ?? 0,
          newAmount: serverPreview.firstPayment.amount,
          targetBillingCadence: serverPreview.targetBillingCadence,
        };
      }
      return {
        direction: "upgrade",
        unusedCredit: serverPreview.unusedCredit ?? 0,
        proratedAmount: serverPreview.proratedFirstPayment ?? serverPreview.firstPayment.amount,
        fullAmount: serverPreview.fullMonthlyAmount ?? serverPreview.newAmount,
        daysRemaining: serverPreview.daysRemaining ?? 0,
      };
    }
    if (clientEstimate) {
      return {
        direction:
          previewChangeType === "downgrade"
            ? "downgrade"
            : previewChangeType === "period_change"
              ? "cadence_change"
              : "upgrade",
        ...(previewChangeType === "downgrade"
          ? {
              trialDays: clientEstimate.daysRemaining,
              savings: clientEstimate.unusedCredit,
              newAmount: clientEstimate.fullAmount,
            }
          : previewChangeType === "period_change"
            ? {
                trialDays: clientEstimate.daysRemaining,
                newAmount: clientEstimate.fullAmount,
                targetBillingCadence: billingPeriod,
              }
            : {
                unusedCredit: clientEstimate.unusedCredit,
                proratedAmount: clientEstimate.proratedAmount,
                fullAmount: clientEstimate.fullAmount,
                daysRemaining: clientEstimate.daysRemaining,
              }),
      } as PlanChangeDisplay;
    }
    return null;
  }, [serverPreview, clientEstimate, previewChangeType, billingPeriod]);

  // Sort tiers by displayOrder and filter to plan changes (upgrades + downgrades)
  const allTiers = [...allTiersRaw].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

  const hasYearlyPlans = allTiers.some((tier) => tier.price.yearly > 0);
  const savingsPercent = Math.max(...allTiers.map((tier) => tier.yearlySavingsPercent ?? 0), 0);

  const availableTiers = allTiers.filter((tier) => {
    const changeType = getPlanChangeType(tier.id, billingPeriod);
    return changeType === "upgrade" || changeType === "downgrade" || changeType === "period_change";
  });

  const handleTierSelect = (tierId: string) => {
    setSelectedTier(tierId);
  };

  const handleConfirmPlanChange = () => {
    if (!selectedTier) {
      return;
    }

    void onUpgrade(selectedTier, billingPeriod);
    onOpenChange(false);
  };

  const handleTierHover = (tierId: string | undefined) => {
    if (activeSubscription && !selectedTier) {
      setHoveredTierId(tierId);
    }
  };

  const isConfirmDisabled =
    isLoading ||
    !selectedTier ||
    Boolean(activeSubscription && (isPreviewLoading || previewError || !serverPreview));

  // Helper function to calculate yearly savings (inline for now)
  // const getYearlySavings = (tier: MembershipTierConfig) => {
  //   if (tier.price.monthly === 0) return null;
  //   return calculateYearlySavings(tier.price.monthly, tier.price.yearly);
  // };

  if (availableTiers.length === 0) {
    return (
      <Dialog onOpenChange={onOpenChange} open={open}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>No Plan Changes Available</DialogTitle>
            <DialogDescription>
              There are no other plans available. Contact our sales team for custom enterprise
              solutions.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4 pt-4">
            <MembershipBadge size="lg" tier={currentTier} />
            <Button className="w-full" onClick={() => onOpenChange(false)}>
              Contact Sales
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader className="space-y-4">
          <div className="space-y-2">
            <DialogTitle className="text-2xl">Change Your Plan</DialogTitle>
            <DialogDescription className="text-base">
              Choose a plan that fits your needs
            </DialogDescription>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-muted-foreground text-sm">Currently on:</span>
            <MembershipBadge tier={currentTier} />
          </div>
        </DialogHeader>

        <div className="space-y-6 pt-4">
          {/* Billing Period Toggle */}
          {hasYearlyPlans && (
            <div className="flex items-center justify-center">
              <Tabs
                className="w-fit"
                onValueChange={(value) => setBillingPeriod(value as "monthly" | "yearly")}
                value={billingPeriod}
              >
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="monthly">Monthly</TabsTrigger>
                  <TabsTrigger className="relative" value="yearly">
                    Yearly
                    {savingsPercent > 0 && (
                      <Badge className="ml-2 h-5 px-1.5 text-xs" variant="secondary">
                        -{savingsPercent}%
                      </Badge>
                    )}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          )}

          {/* Plan Change Preview */}
          {activeSubscription && previewError && previewTargetId && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-destructive text-sm">
              {previewError instanceof Error
                ? previewError.message
                : "Failed to fetch Plan change preview"}
            </div>
          )}

          {activeSubscription &&
            planChangeDisplay &&
            previewTargetId &&
            planChangeDisplay.direction === "upgrade" && (
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">Upgrade Preview</span>
                      {isPreviewLoading && (
                        <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground text-xs">
                      <span>
                        Credit from current plan:{" "}
                        <span className="font-medium text-green-600 dark:text-green-400">
                          -{formatCurrency(planChangeDisplay.unusedCredit)}
                        </span>
                      </span>
                      <ArrowRight className="h-3 w-3" />
                      <span>{planChangeDisplay.daysRemaining} days remaining</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-muted-foreground text-xs line-through">
                      {formatCurrency(planChangeDisplay.fullAmount)}
                    </div>
                    <div className="font-bold text-lg">
                      {formatCurrency(planChangeDisplay.proratedAmount)}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      then {formatCurrency(planChangeDisplay.fullAmount)}/mo
                    </div>
                  </div>
                </div>
              </div>
            )}

          {activeSubscription &&
            planChangeDisplay &&
            previewTargetId &&
            (planChangeDisplay.direction === "downgrade" ||
              planChangeDisplay.direction === "cadence_change") && (
              <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <TrendingDown className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                      <span className="font-medium text-sm">
                        {planChangeDisplay.direction === "cadence_change"
                          ? "Billing Cadence Change Preview"
                          : "Downgrade Preview"}
                      </span>
                      {isPreviewLoading && (
                        <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground text-xs">
                      <Clock className="h-3 w-3" />
                      <span>Current benefits kept for {planChangeDisplay.trialDays} more days</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-lg">
                      {formatCurrency(planChangeDisplay.newAmount)}/
                      {planChangeDisplay.direction === "cadence_change" &&
                      planChangeDisplay.targetBillingCadence === "yearly"
                        ? "yr"
                        : "mo"}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {planChangeDisplay.direction === "cadence_change" ? (
                        <span>Starts with {planChangeDisplay.targetBillingCadence} billing</span>
                      ) : (
                        <>
                          Save{" "}
                          <span className="font-medium text-green-600 dark:text-green-400">
                            {formatCurrency(planChangeDisplay.savings)}
                          </span>
                          /mo
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

          {/* Pricing Cards */}
          <div
            className={cn(
              "grid gap-6",
              availableTiers.length === 1 && "mx-auto max-w-sm grid-cols-1",
              availableTiers.length === 2 && "grid-cols-1 md:grid-cols-2",
              availableTiers.length >= MIN_TIERS_FOR_THREE_COLUMN &&
                "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
            )}
          >
            {availableTiers.map((tier) => (
              <PricingCard
                billingPeriod={billingPeriod}
                changeType={getPlanChangeType(tier.id, billingPeriod)}
                currentTier={currentTier}
                disabled={isLoading}
                hasActiveSubscription={!!activeSubscription}
                isLoading={isLoading && selectedTier === tier.id}
                isPopular={tier.id === "pro"}
                key={tier.id}
                onSelect={() => {
                  handleTierHover(tier.id);
                  handleTierSelect(tier.id);
                }}
                tier={tier}
              />
            ))}
          </div>

          {selectedTier && (
            <div className="flex flex-col gap-3 rounded-lg border bg-muted/20 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm">
                <div className="font-medium">
                  {activeSubscription
                    ? "Ready to accept this Plan change"
                    : "Ready to start checkout"}
                </div>
                <div className="text-muted-foreground">
                  {activeSubscription
                    ? "Review the authoritative preview above before confirming."
                    : "Checkout remains the initial Subscription purchase path."}
                </div>
              </div>
              <Button disabled={isConfirmDisabled} onClick={handleConfirmPlanChange} type="button">
                {isPreviewLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading preview...
                  </>
                ) : activeSubscription ? (
                  "Confirm Plan change"
                ) : (
                  "Continue to checkout"
                )}
              </Button>
            </div>
          )}

          {/* Enterprise CTA */}
          {!availableTiers.find((t) => t.id === "ultimate") && currentTier !== "ultimate" && (
            <div className="rounded-lg border bg-muted/30 p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    <span className="font-medium text-sm">Need Enterprise Features?</span>
                  </div>
                  <div className="text-muted-foreground text-sm">
                    Custom solutions, dedicated support, and unlimited usage for large
                    organizations.
                  </div>
                </div>
                <Button size="sm" variant="outline">
                  Contact Sales
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
